//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/common/service';
import RoutesModel from './routes.model';

/**
 * @service RoutesService
 * @description Service for managing application route definitions and metadata.
 * This service acts as the central repository for all navigable paths and API endpoints defined within the system.
 *
 * It interacts with the `/routes/` backend endpoint to retrieve configuration data that maps URLs to specific features,
 * functions, products, and screens.
 *
 * **Key Responsibilities:**
 * 1. **Route Registry**: Fetching the master list of all available routes in the application.
 * 2. **Access Control Support**: The data retrieved here is often used in conjunction with ACL services to determine if a user has permission to access a specific route.
 * 3. **Dynamic Navigation**: Supporting the generation of menus or sitemaps based on active routes.
 * 4. **Resource Management**: Handling HTTP request lifecycles to prevent memory leaks.
 *
 * **Data Flow:**
 * - **Retrieval**: The application (often during initialization or admin configuration) calls `getAllRoutes`.
 * - **Backend**: The service requests `/routes/` with a large page size to ensure all definitions are loaded.
 * - **Usage**: The returned `RoutesModel` objects are used to configure the Angular Router or validate permissions.
 *
 * @feature 1. Retrieve all defined application routes.
 * @feature 2. Support for large datasets (pageSize=50000) to ensure comprehensive coverage.
 * @feature 3. Robust management of concurrent HTTP requests.
 *
 * @architecture
 * Extends base Service<RoutesModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /routes/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class RoutesService extends Service<RoutesModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/routes/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/routes/');
  }

  /**
   * @method getAllRoutes
   * @async
   * @description Fetch all route definitions from the backend.
   *
   * This method performs a GET request to the service endpoint. It is configured to retrieve
   * a very large number of records (pageSize=50000) to ensure the complete routing table is loaded,
   * ordered by creation date ('new').
   *
   * @returns {Promise<Array<RoutesModel>>} Promise resolving to an array of RoutesModel objects.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request.
   * - Pushes the subscription to `ongoingRequests`.
   *
   * @example
   * routesService.getAllRoutes().then(routes => {
   *   console.log('System Routes:', routes);
   * });
   */
  public getAllRoutes() {
    return new Promise<Array<RoutesModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<RoutesModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=50000&order=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
